/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Subscription Service - API calls for billing and subscription management
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import api from './api'

const subscriptionService = {
  /**
   * Get Stripe configuration status
   * @returns {Promise} Stripe status object
   */
  getStatus: async () => {
    const response = await api.get('/subscription/status')
    return response.data
  },

  /**
   * Get available subscription tiers (public endpoint)
   * @returns {Promise} Array of available tiers
   */
  getAvailableTiers: async () => {
    const response = await api.get('/subscription/tiers')
    return response.data
  },

  /**
   * Get current user's personal subscription
   * @returns {Promise} User's subscription details
   */
  getPersonalSubscription: async () => {
    const response = await api.get('/subscription')
    return response.data
  },

  /**
   * Get organization subscription (requires org admin)
   * @param {string} orgId - Organization ID
   * @returns {Promise} Organization subscription details
   */
  getOrganizationSubscription: async (orgId) => {
    const response = await api.get(`/subscription/organization/${orgId}`)
    return response.data
  },

  /**
   * Get tier details by code (for deeplinks)
   * @param {string} code - Tier code
   * @returns {Promise} Tier details
   */
  getTierByCode: async (code) => {
    const response = await api.get(`/subscription/tiers/${code}`)
    return response.data
  },

  /**
   * Validate a promotion code
   * @param {string} code - Promo code
   * @param {string} tier - Tier to apply it to (optional)
   * @returns {Promise} Validation result with discount details
   */
  validatePromoCode: async (code, tier = null) => {
    const response = await api.post('/subscription/validate-promo', { code, tier })
    return response.data
  },

  /**
   * Create checkout session for personal subscription upgrade
   * @param {Object} params - { tier, billingCycle, quantity, promotionCode }
   * @returns {Promise} Checkout session with URL
   */
  createCheckoutSession: async ({ tier, billingCycle = 'monthly', quantity, promotionCode }) => {
    const response = await api.post('/subscription/checkout', {
      tier: tier.toUpperCase(),
      billingCycle,
      quantity,
      promotionCode
    })
    return response.data
  },

  /**
   * Create checkout session for organization subscription (org admin only)
   * @param {string} orgId - Organization ID
   * @param {Object} params - { tier, billingCycle, quantity }
   * @returns {Promise} Checkout session with URL
   */
  createOrgCheckoutSession: async (orgId, { tier, billingCycle = 'monthly', quantity }) => {
    const response = await api.post(`/subscription/organization/${orgId}/checkout`, {
      tier: tier.toUpperCase(),
      billingCycle,
      quantity
    })
    return response.data
  },

  /**
   * Create customer portal session for personal billing management
   * @param {string} returnUrl - URL to return to after portal
   * @returns {Promise} Portal session with URL
   */
  createPortalSession: async (returnUrl) => {
    const response = await api.post('/subscription/portal', { returnUrl })
    return response.data
  },

  /**
   * Create customer portal session for organization billing (org admin only)
   * @param {string} orgId - Organization ID
   * @param {string} returnUrl - URL to return to after portal
   * @returns {Promise} Portal session with URL
   */
  createOrgPortalSession: async (orgId, returnUrl) => {
    const response = await api.post(`/subscription/organization/${orgId}/portal`, {
      returnUrl
    })
    return response.data
  },

  /**
   * Cancel subscription (at period end by default)
   * @param {boolean} immediately - Cancel immediately instead of at period end
   * @returns {Promise} Cancellation confirmation
   */
  cancelSubscription: async (immediately = false) => {
    const response = await api.post('/subscription/cancel', { immediately })
    return response.data
  },

  /**
   * Reactivate subscription that was set to cancel
   * @returns {Promise} Reactivation confirmation
   */
  reactivateSubscription: async () => {
    const response = await api.post('/subscription/reactivate')
    return response.data
  },

  /**
   * Change subscription to a different tier (for existing subscribers)
   * @param {Object} params - { tierCode, billingCycle }
   * @returns {Promise} Plan change result
   */
  changePlan: async ({ tierCode, billingCycle = 'monthly' }) => {
    const response = await api.post('/subscription/change-plan', {
      tierCode: tierCode.toUpperCase(),
      billingCycle
    })
    return response.data
  }
}

export default subscriptionService
